"use client";

import type { ElementType, ReactNode } from "react";

interface NoTranslateProps {
  children: ReactNode;
  /** Element tag (default: span). Use "div" for block contexts. */
  as?: ElementType;
  /** Additional className passed through to the wrapper */
  className?: string;
  /** Test ID forwarded to the wrapper element (for E2E selectors) */
  "data-testid"?: string;
}

/**
 * Marks its children as untranslatable for browser translation extensions
 * (Chrome translate, Google Translate, etc.).
 *
 * Used for values that must remain byte-exact regardless of the user's
 * translation locale: legal names, IBANs, BICs, account numbers,
 * blockchain addresses, transaction IDs, currency codes (ISO 4217), email
 * addresses, phone numbers, formatted amounts.
 *
 * Production incident reference: Chrome translated "POLLOCK NOAH" →
 * "POLLO NOÈ" when an Italian user viewed the account-details modal —
 * "POLLO" means "chicken". The displayed name no longer matched the
 * legal account holder, breaking outgoing transfers.
 *
 * Implementation:
 * - `translate="no"` is the standard HTML attribute (Chrome, Edge,
 *   Firefox respect it).
 * - `class="notranslate"` is the older Google-Translate convention.
 * - Both are belt-and-suspenders since some translation flows ignore one
 *   or the other.
 *
 * Usage:
 *   <NoTranslate>{accountHolderName}</NoTranslate>
 *   <NoTranslate>{iban}</NoTranslate>
 *   <NoTranslate as="div">{multilineAddress}</NoTranslate>
 *
 * Do NOT use for translation-key strings (`{t("foo")}`) — those should
 * always translate. NoTranslate is for value renders that originate from
 * user input, financial IDs, ISO codes, or other byte-exact data.
 *
 * See `.claude/rules/web/translation-tolerance.md` for the full policy.
 */
export function NoTranslate({
  children,
  as: Tag = "span",
  className,
  "data-testid": dataTestId,
}: NoTranslateProps) {
  return (
    <Tag
      translate="no"
      className={className ? `notranslate ${className}` : "notranslate"}
      data-testid={dataTestId}
    >
      {children}
    </Tag>
  );
}
